import { useState } from 'react';
import { Wifi } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { PrinterConfigApi } from '@/api/printing';
import { toast } from 'sonner';
import { errOf } from '@/api/result';
import { appErrorMessage } from '@/api/errors';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  impersonatedUserId: string | null;
}

export function NetworkPrinterDialog({ open, onOpenChange, impersonatedUserId }: Props) {
  const [printerIp, setPrinterIp] = useState('');
  const [printerPort, setPrinterPort] = useState('9100');

  const handleSave = async () => {
    if (!printerIp.trim()) {
      toast.error('Veuillez saisir une adresse IP');
      return;
    }
    if (!impersonatedUserId) {
      toast.error('Aucun client sélectionné');
      return;
    }
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, tenant_id')
        .eq('user_id', impersonatedUserId)
        .single();
      if (!profile) throw new Error('Profil non trouvé');
      if (!profile.tenant_id) throw new Error('Tenant du client introuvable');

      // Imprimante ROUTABLE : le routeur d'impression ne lit que
      // tenant_printers (connection_type 'network') — la row hardware_configs
      // ci-dessous n'est qu'un inventaire admin, elle n'imprime rien.
      const printerResult = await PrinterConfigApi.createPrinter(
        {
          name: `Imprimante réseau (${printerIp.trim()})`,
          printer_type: 'receipt',
          connection_type: 'network',
          ip_address: printerIp.trim(),
          port: Number(printerPort) || 9100,
          paper_width: 80,
          auto_cut: true,
          is_active: true,
        },
        profile.tenant_id,
      );
      if (!printerResult.ok) throw new Error(appErrorMessage(errOf(printerResult)));

      // Strangler lot 2 : via PrinterConfigApi
      const result = await PrinterConfigApi.createHardwareConfig({
        profile_id: profile.id,
        device_type: 'printer',
        device_name: `Imprimante réseau (${printerIp})`,
        device_brand: 'Réseau',
        device_category: 'standalone',
        connection_type: 'ethernet',
        ip_address: printerIp.trim(),
        port: printerPort || '9100',
        protocol: 'ESC/POS',
        encoding: 'UTF-8',
        paper_width: '80',
        auto_cut: true,
        cash_drawer: false,
        notes: 'Configurée via panneau admin',
      });
      if (!result.ok) throw new Error(appErrorMessage(errOf(result)));
      toast.success(`Imprimante réseau ${printerIp}:${printerPort} enregistrée !`);
      onOpenChange(false);
      setPrinterIp('');
      setPrinterPort('9100');
    } catch (err: any) {
      toast.error(`Erreur : ${err.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5 text-primary" />
            Imprimante réseau
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="printer-ip">Adresse IP de l'imprimante</Label>
            <Input
              id="printer-ip"
              placeholder="192.168.1.100"
              value={printerIp}
              onChange={(e) => setPrinterIp(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="printer-port">Port</Label>
            <Input
              id="printer-port"
              placeholder="9100"
              value={printerPort}
              onChange={(e) => setPrinterPort(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Assurez-vous que l'imprimante est allumée et connectée au même réseau Wi-Fi/Ethernet.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
